import React, { useState, useEffect, useCallback } from 'react';
import { Typography } from '@mui/material';
import styles from "./MoodQuiz.module.css";

import ChevronUpSVG from '../../resources/chevron/chevron-up.svg';
import ChevronDownSVG from '../../resources/chevron/chevron-down.svg';
import ForrestPNG from '../../resources/MoodQuiz/game-background.png';
import MessageSVG from '../../resources/MoodQuiz/message-chat-circle.svg';
import GameSVG from '../../resources/MoodQuiz/gaming-pad-01.svg';
import PrinterSVG from '../../resources/MoodQuiz/printer.svg';
import SignUpBannerFooter from "../../components/Footer/SignUpBannerFooter";
import {useLocation} from "react-router-dom";
import {useAuth} from "../../services/AuthProvider";

interface Question {
  id: number;
  question: string;
}

const questionStems: Question[] = [
  { id: 1, question: 'Over the last two weeks have you been feeling down, depressed, irritable, or hopeless?' },
  { id: 2, question: 'Over the last two weeks have you had little interest or pleasure in doing things?' },
  { id: 3, question: 'Over the last two weeks have you had trouble falling asleep, staying asleep, or have you been sleeping too much?' },
  { id: 4, question: 'Over the last two weeks have you had poor appetite, weight loss, or have you been overeating?' },
  { id: 5, question: 'Over the last two weeks have you been feeling tired, or have you had little energy?' },
  { id: 6, question: 'Over the last two weeks have you been feeling bad about yourself - or feeling that you are a failure, or that you have let yourself or your family down?' },
  { id: 7, question: 'Over the last two weeks have you had trouble concentrating on things like schoolwork, reading, or watching TV?' },
  { id: 8, question: 'Over the last two weeks have you been moving or speaking so slowly that other people could have noticed? Or the opposite - being so fidgety or restless that you were moving around a lot more than usual?' },
  { id: 9, question: 'Over the last two weeks have you had thoughts that you would be better off dead, or of hurting yourself in some way?' },
];

interface QuizIntroProps {
  onStartQuiz: () => void;
}

const MoodQuizIntro: React.FC<QuizIntroProps> = ({ onStartQuiz }) => (
  <div>
    <section id="MoodQuizIntroduction" className={styles.MoodQuizIntroduction}>
      <Typography variant="subtitle1">Let's check your mood</Typography>
      <div style={{ height: '24px' }} />
      <Typography variant="body1">
        <br/>
        <span>We all experience bad days and feel low, stressed or worried from time to time. When these feelings go on for a while it may be that you are experiencing depression or anxiety, or both.</span>
        <br/><br/>
        <span>The Patient Health Questionnaire - Adolescent (PHQ-A) is one of the tools used to screen for the presence and severity of depression and to monitor response to treatment. Take this self-test to check if you're showing any of the common signs of depression. This test does not replace a health professional and it is not a diagnosis.</span>
        <br/><br/>
        <span>Note: your responses to this questionnaire are anonymous. Your scores are not recorded and we do not capture any information that can identify you.</span>
        <br/>
      </Typography>
    </section>
    <section id="MoodQuizStartButtonRow" className={styles.MoodQuizIntroRow}>
      <button className={`PrimaryButton ${styles.MoodQuizStartButton}`} onClick={onStartQuiz}>Start quiz</button>
    </section>
    <section id="MoodQuizMessage" className={styles.MoodQuizIntroRow}>
      <div className={styles.MoodQuizIntroCard}>
        <div style={{maxWidth: '800px'}}>
          <Typography variant="h2">Seeking help?</Typography>
          <br/>
          <Typography variant="body1" style={{color: '#FFF'}}>If you are having thoughts of harming yourself, it's important to reach out and speak with someone who is trained to provide immediate support.</Typography>
          <br/>
          <div className={styles.MoodQuizIntroCardContact}>
            <div><a href="tel:0508477279">0508 4 SPARX</a> (0508 477 279)</div>
            <div>or text <a href="sms:3110">3110</a> for free</div>
            <div style={{marginTop: 35}}><a href="tel:111">Call 111</a> if you or someone else might be unsafe right now.</div>
          </div>
          <br/>
        </div>
      </div>
    </section>
    <br/><br/>
  </div>
);

const MoodQuizQuestions: React.FC<{ onCompleteQuiz: () => void }> = ({ onCompleteQuiz }) => {
  const [currentQuestion, setCurrentQuestion] = useState(0);

  const handleAnswerSelect = (questionId: number, answer: string) => {
    localStorage.setItem(`moodQuizAnswer_${questionId}`, answer);
    if (currentQuestion < questionStems.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    } else {
      onCompleteQuiz();
    }
  };

  const handleProgressClick = (index: number) => {
    setCurrentQuestion(index);
  };

  return (
    <div style={{marginBottom: 50}}>
      <div className={styles.MoodQuizProgressDots}>
        {questionStems.map((_, index) => (
          <div
            key={index}
            className={`${styles.MoodQuizProgressDot} ${index === currentQuestion ? styles.MoodQuizProgressActiveDot : ''}`}
            onClick={() => handleProgressClick(index)}
          ></div>
        ))}
      </div>
      <div key={currentQuestion} className={styles.MoodQuizCard}>
        <Typography variant="h4">Question {questionStems[currentQuestion].id}</Typography>
        <Typography variant="body1">{questionStems[currentQuestion].question}</Typography>
        <div className={styles.MoodQuizOptions}>
          <button onClick={() => handleAnswerSelect(questionStems[currentQuestion].id, 'Not at all')}>
            <Typography variant="button">Not at all</Typography>
          </button>
          <button onClick={() => handleAnswerSelect(questionStems[currentQuestion].id, 'Several days')}>
            <Typography variant="button">Several days</Typography>
          </button>
          <button onClick={() => handleAnswerSelect(questionStems[currentQuestion].id, 'More than half the days')}>
            <Typography variant="button">More than half the days</Typography>
          </button>
          <button onClick={() => handleAnswerSelect(questionStems[currentQuestion].id, 'Nearly every day')}>
            <Typography variant="button">Nearly every day</Typography>
          </button>
        </div>
      </div>
    </div>
  );
};

const MoodQuizResult: React.FC = () => {
  const [showAnswers, setShowAnswers] = useState(false);
  const [result, setResult] = useState({ title: '', detail: '' });

  const toggleAnswers = () => {
    setShowAnswers(!showAnswers);
  };

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  };

  const handlePrint = ()  => {
    setShowAnswers(true);

    // wait 0.3 seconds for the expanded answers
    setTimeout(() => {
      window.print();
    }, 300);
  }

  const calculateScore = () => {
    let totalScore = 0;
    questionStems.forEach((q) => {
      const answer = localStorage.getItem(`moodQuizAnswer_${q.id}`);
      if (answer === 'Not at all') totalScore += 0;
      else if (answer === 'Several days') totalScore += 1;
      else if (answer === 'More than half the days') totalScore += 2;
      else if (answer === 'Nearly every day') totalScore += 3;
    });

    const answer_9 = localStorage.getItem(`moodQuizAnswer_9`);
    if (answer_9 === 'More than half the days' || answer_9 === 'Nearly every day') {
      totalScore = 100;
    }

    const resultCategories = [
      { scoreThreshold: 5, title: 'Less than 5', detail: `Thanks for doing the mood quiz. It sounds like you are mostly doing well. SPARX was made to help young people who feel stressed or down, but SPARX skills can be useful for everyone. If you want to know how to cope with life's ups and downs, <a href="https://sparx-nz.webflow.io/the-game" style="color: #2D94E8">give SPARX a go now</a>.` },
      { scoreThreshold: 10, title: 'Between 5 and 9', detail: `Thanks for doing the mood quiz. It sounds like you are doing well, but you might be a bit stressed or down from time-to-time. SPARX will show you skills to help you feel better. <a href="https://sparx-nz.webflow.io/the-game" style="color: #2D94E8">Give SPARX a go now.</a>` },
      { scoreThreshold: 15, title: 'Between 10 and 14', detail: `Thanks for doing the mood quiz. It sounds like you are quite stressed and down. SPARX is a program made for young people like you, to help you feel better. <a href="https://sparx-nz.webflow.io/the-game" style="color: #2D94E8">Give SPARX a go now.</a>` },
      { scoreThreshold: 28, title: 'Between 15 and 27', detail: `Thanks for doing the mood quiz. It sounds like you're really stressed and down. SPARX could give you some skills on how to feel better but it might not be enough. If you don't start feeling better soon, you should talk to someone about it. <a href="https://sparx-nz.webflow.io/contact">A list of places where you can find more help is on the SPARX website.</a>`},
      { scoreThreshold: 101, title: 'Feedback', detail: `Thanks for doing the mood quiz. You said you've had a lot of thoughts of hurting yourself, or that you'd be better off dead. These thoughts are common when people feel down, angry or really stressed. Please speak to an adult right away. A list of places where you can find more help and advice on <a href="https://sparx-nz.webflow.io/contact" style="color: #2D94E8">how to ask for help</a> is on the SPARX website. `}
    ];

    for (const category of resultCategories) {
      if (totalScore < category.scoreThreshold) {
        setResult({ title: category.title, detail: category.detail });
        break;
      }
    }
  };

  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
    calculateScore();
  }, []);

  return (
    <>
      <div className={styles.MoodQuizResultCard}>
        <div className={styles.MoodQuizResultCardScore}>
          <Typography variant="h4" style={{textAlign: 'left'}}>Your quiz score</Typography>

          <div className="ShowFor768BigScreenOnly">
            <div className={styles.MoodQuizResultCardPrint} onClick={handlePrint}>
              <Typography variant="body1" style={{color: 'black', textAlign: 'right', fontWeight: '300'}}>Print quiz result</Typography>
              &nbsp;&nbsp;&nbsp;<img src={PrinterSVG} alt="printer"/>
            </div>
          </div>
        </div>

        <div>
          <Typography className={styles.MoodQuizResultTitle}>{result.title}</Typography>
          <Typography variant="body1" style={{fontSize: '18px !important', marginTop: '12px'}}>
            <span dangerouslySetInnerHTML={{ __html: result.detail }} />
            <br/>
          </Typography>
        </div>

        <>
          <div style={{padding: '25px 0', margin: 'auto'}}>
            <button className={`PrimaryButton ${styles.MoodQuizShowAnswerButton}`} onClick={toggleAnswers}>
              <Typography variant="subtitle2" style={{textAlign: 'center', color: '#FFF'}}>
                {showAnswers ? "Minimise answers" : "Show my answers"}
              </Typography>
              &nbsp;
              {showAnswers ? <img src={ChevronUpSVG} alt="up"/> : <img src={ChevronDownSVG} alt="down"/>}
            </button>
          </div>

          <img src={ForrestPNG} alt="forrest" className={styles.MoodQuizForrestPNG} style={{ display: showAnswers ? 'none' : 'block' }} />
        </>
      </div>

      {showAnswers && (
        <>
          {questionStems.map((q) => (
            <div key={q.id} className={styles.MoodQuizResultCard}>
              <Typography variant="h4">Question {q.id}</Typography>
              <Typography variant="body1">{q.question}</Typography>
              <div className={styles.MoodQuizOptions}>
                <div className={styles.MoodQuizSelectedOption}>
                  <Typography variant="button" style={{color: 'white'}}>{localStorage.getItem(`moodQuizAnswer_${q.id}`)}</Typography>
                </div>
              </div>
            </div>
          ))}
          <div style={{ display: 'flex', marginTop: '20px'}}>
            <button className={`PrimaryButton ${styles.MoodQuizBackTopButton}`} onClick={scrollToTop}>
              Back to top &nbsp;&nbsp;
              <img src={ChevronUpSVG} alt="up"
                   style={{transform: 'scale(1.5)', filter: 'brightness(0) saturate(100%) invert(100%) sepia(5%) saturate(5%) hue-rotate(49deg) brightness(116%) contrast(100%)'}}/>
            </button>
          </div>
        </>
      )}

      <section id="MoodQuizRecommendations" className={styles.MoodQuizRecommendSection}>
        <div className={styles.MoodQuizRecommendSectionView}>
          <Typography variant="h6">RECOMMENDATIONS</Typography>
          <Typography variant="subtitle1" style={{textAlign: 'left', padding: '12px 0'}}>Take these next steps</Typography>
          <div className={styles.MoodQuizRecommendCardsRow}>
            <div className={styles.MoodQuizRecommendCard}>
              <img src={MessageSVG} alt="message" />
              <Typography variant="h4">Talk to someone</Typography>
              <Typography variant="body1">
                <br/>
                <span>A good place to start is with someone you trust - it could be a guidance counsellor, teacher, school nurse or family doctor.</span>
                <br/><br/>
                <span>If you feel like hurting yourself, if you're feeling really bad, or if things don't improve, don't wait to ask for help, call <a href="tel:111" style={{color: '#2D94E8'}}>111</a>.</span>
                <br/>
              </Typography>
            </div>

            <div className={styles.MoodQuizRecommendCard}>
              <img src={GameSVG} alt="game" />
              <Typography variant="h4">Try out SPARX</Typography>
              <Typography variant="body1">
                <br/>
                <span>SPARX is designed to help you learn and practise useful skills to help you feel better. It's made for young people like you who may be feeling down, stressed or worried.</span>
                <br/><br/>
                <span><a href="https://www.sparx.org.nz/start-sparx" style={{color: '#2D94E8'}}>Download SPARX</a></span>
                <br/>
              </Typography>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

const MoodQuiz: React.FC = () => {
  const {isLoggedIn} = useAuth();
  const [step, setStep] = useState(0);
  const handleStartQuiz = useCallback(() => setStep(1), []);
  const handleCompleteQuiz = useCallback(() => setStep(2), []);
  const location = useLocation();
  const [key, setKey] = useState(0); // Change key to force re-render

  useEffect(() => {
    // reset the survey
    setStep(0);
    for (let questionId = 1; questionId <= 9; questionId++) {
      localStorage.removeItem(`moodQuizAnswer_${questionId}`);
    }

    // Reset the key when the component mounts or the location changes
    setKey(prevKey => prevKey + 1);
  }, [location]);

  return (
    <div key={key} className={styles.MoodQuiz} data-testid="mood">
      <section id="MoodQuizTitle" className={styles.MoodQuizTitle}>
        <Typography variant="h6">SUPPORT</Typography>
        <div style={{ height: '8px' }}></div>
        <Typography variant="h1">Mood Quiz</Typography>
      </section>

      {step === 0 && <MoodQuizIntro onStartQuiz={handleStartQuiz} />}
      {step === 1 && <MoodQuizQuestions onCompleteQuiz={handleCompleteQuiz} />}
      {step === 2 && <MoodQuizResult />}

      {!isLoggedIn && <SignUpBannerFooter />}
    </div>
  );
};

export default MoodQuiz;
